import { useCallback, useMemo, useState } from 'react';
import { useFileUrlToTypeMapping } from '@pflegenavi/web-components';
import { useGetReceiptImagesWithTenant } from '@pflegenavi/service-provider-frontend/api';
import { useReceiptBatchStore } from '../ReceiptBatchContext';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const usePreviewImageIds = () => {
  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );
  const [shownImageId, setShownImageId] = useState<string | undefined>(
    undefined
  );
  const [imageUrlToTitleMap, setImageUrlToTitleMap] = useState<
    Map<string, string | undefined>
  >(new Map());

  const selectedNursingHome = useReceiptBatchStore((state) => {
    return state.nursingHomes?.find(
      (n) => n.nursingHomeId === state?.formik?.values?.nursingHomeId
    );
  });

  const useGetReceiptUrls = useGetReceiptImagesWithTenant({
    tenant: selectedNursingHome?.tenant ?? '',
  });
  const { data: receiptImagesObject, isPending: imageUrlsLoading } =
    useGetReceiptUrls(receiptImageIds);

  const receiptImageUrls: string[] | undefined = useMemo(() => {
    if (receiptImagesObject) {
      return Object.values(receiptImagesObject)
        .filter((item) => item !== undefined)
        .map((item) => item?.toString() ?? '');
    }
    return undefined;
  }, [receiptImagesObject]);

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const setPreviewImageIds = useCallback((imgIds: string[] | undefined) => {
    setReceiptImageIds(imgIds);
  }, []);

  const setTitleForImage = useCallback(
    (id: string, title: string | undefined) => {
      setImageUrlToTitleMap((prev) => {
        const url = receiptImagesObject?.[id];
        if (url !== undefined) {
          const newMap = new Map(prev);
          newMap.set(url, title);
          return newMap;
        }
        return prev;
      });
    },
    [receiptImagesObject]
  );

  return {
    enlargeImageGalleryProps: {
      loading: imageUrlsLoading,
      open: Boolean(shownImageId),
      handleClose: () => setShownImageId(undefined),
      initialSelectedIndex: shownImageId
        ? receiptImageIds?.indexOf(shownImageId) ?? 0
        : 0,
      imageUrls: receiptImageUrls || [],
      imageUrlToTypeMap: imageUrlToTypeMap,
      imageUrlToTitleMap: imageUrlToTitleMap,
    },
    setPreviewImageIds,
    setShownImageId,
    setTitleForImage,
  };
};
